import * as React from "react";
import {
  Card,
  Typography,
  Theme,
  makeStyles,
  createStyles,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Hidden,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
// import BatteryCharging50Icon from "@material-ui/icons/BatteryCharging50";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import ErrorIcon from "@material-ui/icons/Error";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import TruckIcon from "../../assets/icons/TruckIcon";
import InstallationModel from "../../core/models/Installation";
import {
  formatDateTime,
  formatDateTimeBr,
  getDateFromTimezonePtBrToGMT,
} from "../../helpers/datetime";
import TankModel from "../../core/models/Tank";
import clsx from "clsx";
import TankIndicator from "../TankIndicator";
import TankSVG from "../../assets/diagrams/tank";
import {
  calculateLevelPercentage,
  getLevelColor,
  checkForLevelDataDelay,
  checkTripPlanned,
  checkTripInProgress,
  RenderInstallationTankWarningsTooltip,
  RenderTanksWarnings,
} from "../../helpers/installationTankHelpers";
import SimpleLink from "../SimpleLink";
import DelayIcon from "../DelayIcon";
import PauseIcon from "@material-ui/icons/PauseCircleFilled";
import { formatValue } from "../../helpers/utility";
import { OK_LEVEL_STATUS } from "../../core/models/LevelStatus";
import { EMPTY_STRING_LABEL } from "../../core/constants/appGlobals";
import DottedLine from "../DottedLine";
import { IInstallationRadix } from "../../core/contracts/IInstallation";
import InstallationGroup from "../../core/models/InstallationGroup";
import HelpIcon from "../HelpIcon";
import useAuth from "../../core/hooks/useAuth";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "placeSelf": "flex-start",
      "width": "31.5%",
      [theme.breakpoints.only("lg")]: {
        width: "45% !important",
      },
      [theme.breakpoints.only("md")]: {
        width: "52% !important",
      },
      [theme.breakpoints.only("sm")]: {
        width: "80% !important",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
        placeSelf: "center",
        marginBottom: theme.spacing(1),
      },
      "& .MuiAccordion-root.Mui-expanded": {
        margin: 0,
      },
      background: theme.customPaper.background.cardBackgroundLightUltraDarkContrastWhite
    },

    cardWrapper: {
      display: "flex",
      flexDirection: "column",
      paddingTop: theme.spacing(2),
    },

    titleWrapper: {
      padding: theme.spacing(0, 2),
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      fontSize: "1.333rem",
    },

    infoButton: {
      margin: "0 auto",
    },

    captionWrapper: {
      "display": "flex",
      "justifyContent": "space-between",
      "alignItems": "center",

      "& .truckIcon": {
        "marginRight": 2,
        "width": "1.5em",

        "&:not(.secondary)": {
          color: theme.palette.text.secondary,
        },
      },

      "& .caption": {
        fontSize: "0.667rem",
        fontWeight: "600",
        color: theme.palette.text.secondary,
      },
      "& .alt-color": {
        color: theme.palette.primary.main,
      },
    },

    divider: {
      margin: theme.spacing(0.5, 3),
      width: "2px",
    },

    contentWrapper: {
      "padding": theme.spacing(2, 3, 6, 1.5),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1, 1, 4, 1),
      },
      "color": theme.palette.text.secondary,
      "minHeight": 230,

      "& .name-wrapper": {
        "marginBottom": theme.spacing(1),

        "& .label": {
          fontSize: "1.1rem",
          [theme.breakpoints.down("xs")]: {
            fontSize: "0.8rem",
          },
        },

        "& .name": {
          fontSize: "1.5rem",
          fontWeight: "bold",
          maxWidth: "85%",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
        },
      },

      "& .main-content": {
        "display": "flex",

        "& .grid-wrapper": {
          flex: 1,
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridColumnGap: theme.spacing(2),
          [theme.breakpoints.down("xs")]: {
            gridColumnGap: theme.spacing(1),
          },
        },

        "& .window-section": {
          gridColumn: "1 / -1",
          marginTop: theme.spacing(1),
        },

        "& .window-divider": {
          border: "none",
          borderTop: `1px solid ${theme.customPalette.border.gray}`,
          width: "90%",
          margin: theme.spacing(0, 0, 1.5, 0),
        },

        "& .window-title": {
          fontSize: "1.1rem",
          fontWeight: "bold",
          marginBottom: theme.spacing(1.5),
          color: theme.customPalette.text.primary,
        },

        "& .window-row": {
          display: "flex",
          columnGap: theme.spacing(3),
          [theme.breakpoints.down("xs")]: {
            columnGap: theme.spacing(1.5),
          },
        },

        "& .window-date": {
          display: "flex",
          flexDirection: "column",
          lineHeight: 1.3,
        },

        "& .flex-wrapper": {
          display: "flex",
          flexDirection: "column",
        },

        "& .text": {
          color: "#717171",
        },

        "& .hour-mobile": {
          width: "100%",
          display: "flex",
          justifyContent: "center",
        },

        "& .align-start": {
          alignItems: "flex-start",
        },

        "& .fix-width": {
          width: "200px",
          [theme.breakpoints.down("xs")]: {
            width: "unset",
          }
        },

        "& .label": {
          "fontSize": "1.1rem",
          [theme.breakpoints.down("xs")]: {
            fontSize: "0.8rem",
          },
          // "width": "max-content",
          "display": "flex",
          "alignItems": "flex-start",

          "& svg": {
            marginLeft: theme.spacing(0.25),
          },
        },

        "& .value": {
          fontSize: "1.65rem",
          [theme.breakpoints.down("xs")]: {
            fontSize: "1.25rem",
          },
        },

        "& .main-value": {
          fontSize: "2.5rem",
          [theme.breakpoints.down("xs")]: {
            fontSize: "1.75rem",
          },
        },
      },
    },

    tankWrapper: {
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "flex-end",
      "flexShrink": 0,
      // "marginLeft": theme.spacing(5),
      [theme.breakpoints.down("xs")]: {
        marginLeft: 30,
        minHeight: 150,
      },

      "& svg": {
        height: "100%",
        width: "auto",
        [theme.breakpoints.down("xs")]: {
          height: "80%",
        },
      },

      "& span": {
        fontSize: "0.7rem",
      },

      "& .labels": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100px",
      },
    },

    PSIpressure: {
      display: "flex",
      alignContent: "start",
      marginTop: 5,
      fontWeight: "bold",
      justifyContent: "center",
      [theme.breakpoints.down("xs")]: {
        width: "100px",
      },
    },

    totalStorage: {
      [theme.breakpoints.down("xs")]: {
        width: "100px",
      },
    },

    expansionWrapper: {
      "backgroundColor": theme.customPaper.background.cardBackgroundLightUltraDarkContrastWhite,
      "& .MuiAccordionSummary-content.Mui-expanded": {
        margin: 0,
      },

      "& .MuiAccordionSummary-root": {
        "backgroundColor": theme.customPaper.background.cardBackgroundLightContrast,
        "maxHeight": "48px",
        "minHeight": "48px",

        "& .summaryWrapper": {
          "width": "100%",
          "display": "flex",
          "justifyContent": "space-between",
          "alignItems": "center",

          "& div": {
            "display": "flex",
            "align-items": "center",
          },

          "& .alertIcon": {
            color: theme.palette.error.main,
          },
        },

        "& span": {
          fontSize: "1rem",
          color: theme.palette.text.secondary,
          fontWeight: "bold",
          padding: theme.spacing(0, 1.5, 0, 0),
        },

        "& strong": {
          fontSize: "1.778rem",
          color: theme.palette.text.secondary,
          fontWeight: "600",
          padding: theme.spacing(0, 0.5, 0, 0),
        },
      },

      "& .MuiAccordionDetails-root": {
        padding: theme.spacing(1, 0),
      },
    },

    tankDetailsWrapper: {
      "width": "100%",
      "fontWeight": "bold",
      "color": theme.palette.text.secondary,
      background: theme.customPaper.background.cardBackgroundLightUltraDarkContrastWhite,

      "& span": {
        textAlign: "center",
        fontSize: "0.667rem",
      },

      "& .level": {
        fontSize: "1.111rem",
        color: theme.customPalette.text.boldgrey,
        [theme.breakpoints.down("xs")]: {
          fontSize: "0.8rem",
        },
      },

      "& .pressure": {
        fontSize: "0.778rem",
      },

      "& .MuiGrid-item": {
        padding: theme.spacing(0.5, 1),
        alignSelf: "center",
        textAlign: "center",
      },

      "& .alertIcon": {
        color: theme.palette.error.main,
      },

      "& .opaque": {
        opacity: 0.5,
      },
    },

    spanMinWidth: {
      minWidth: 85,
      [theme.breakpoints.down("xs")]: {
        minWidth: "unset",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },

    blurred: {
      filter: "blur(8px)",
      pointerEvents: "none",
      userSelect: "none",
    },
  })
);

type HomeCardProps = {
  installation: InstallationModel;
  radixInstallation: IInstallationRadix | undefined;
  customerId: string;
  radixGroup?: InstallationGroup;
  favoriteGroup?: boolean;
};

function HomeCard({
  installation,
  radixInstallation,
  customerId,
  radixGroup,
  favoriteGroup,
}: HomeCardProps) {
  const authProvider = useAuth();
  const showWindowInfo = true;
  const blurRestrictedData = Boolean(authProvider.isFreeUser);
  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [nowDate, setNowDate] = React.useState(new Date());
  const [hasAnyPermissionOnGroup, setHasAnyPermissionOnGroup] = React.useState(false);
  const [onHour, setOnHour] = React.useState(true);
  const [infoModalOpen, setInfoModalOpen] = React.useState(false);

  React.useEffect(() => {
    setHasAnyPermissionOnGroup(radixGroup?.permissionList?.length != 0);
  }, [radixGroup]);

  const classes = useStyles();

  const thresholds = {
    veryLow: installation.outage_level_su,
    low: installation.reserve_level_su,
    over: installation.inventory_capacity_su,
  };

  const handleSwitch = () => {
    setOnHour(!onHour);
  };

  const delay = checkForLevelDataDelay(installation.last_rtu_reading_date);

  const getTankLabelColor = (tank: TankModel) => {
    // only changes color when it's a alert
    if (tank.tank_level_status && tank.tank_level_status !== OK_LEVEL_STATUS) {
      return getLevelColor(tank.tank_level_status);
    }

    if (tank.tank_level_su && tank.outage_level && tank.tank_level_su <= tank.outage_level) {
      return getLevelColor(tank.tank_level_su, thresholds);
    }

    return "none";
  };

  const RenderTank = (tank: TankModel) => {
    const [endDate, setEndDate] = React.useState(new Date());

    const delay = checkForLevelDataDelay(tank.last_rtu_reading_date);
    const thresholds = {
      veryLow: tank.outage_level,
      over: tank.tank_capacity_su,
    };
    const currentPercentage = calculateLevelPercentage(tank.tank_level_su, tank.tank_capacity_su);

    const warnings = RenderInstallationTankWarningsTooltip(
      {
        lastReadingDate: tank.last_rtu_reading_date
          ? new Date(
              new Date(tank.last_rtu_reading_date).getTime() +
                new Date(tank.last_rtu_reading_date).getTimezoneOffset() * 60000
            )
          : undefined,
        level: tank.tank_level_su,
        levelStatus: tank.tank_level_status,
        capacity: tank.tank_capacity_su,
        veryLowLevel: tank.outage_level,
      },
      endDate
    );

    return (
      <Grid container key={tank.tank_id}>
        <Grid item xs={1} sm>
          {warnings && (
            <Tooltip title={warnings} onOpen={() => setEndDate(new Date())}>
              {checkForLevelDataDelay(tank.last_rtu_reading_date) ? (
                <PauseIcon fontSize="small" className={"alertIcon"} />
              ) : (
                <ErrorIcon fontSize="small" className={"alertIcon"}></ErrorIcon>
              )}
            </Tooltip>
          )}
        </Grid>
        <Grid item xs={3} sm className={classes.spanMinWidth}>
          <Hidden xsDown>
            <span>{tank.tank_id}</span>
          </Hidden>
          <Hidden smUp>
            <Tooltip title={tank.tank_id}>
              <span>{tank.tank_id}</span>
            </Tooltip>
          </Hidden>
        </Grid>
        <Tooltip title={getDateFromTimezonePtBrToGMT(tank.last_rtu_reading_date)}>
          <Grid item xs={4} sm={4} className={clsx(delay && "opaque")}>
            <TankIndicator
              status={tank.tank_level_status}
              capacity={tank.tank_capacity_su}
              level={tank.tank_level_su}
              thresholds={thresholds}
            />
          </Grid>
        </Tooltip>
        <Grid item xs={1} sm>
          <Tooltip title={getDateFromTimezonePtBrToGMT(tank.last_rtu_reading_date)}>
            <span
              style={{
                color: getTankLabelColor(tank),
              }}
              className={clsx("level", delay && "opaque")}
            >
              {currentPercentage}%
            </span>
          </Tooltip>
        </Grid>
        <Grid item xs={2} sm className={classes.spanMinWidth}>
          <Hidden smUp>
            <Tooltip title={formatValue(tank.tank_level_su)}>
              <span className="level">{formatValue(tank.tank_level_su)}</span>
            </Tooltip>
          </Hidden>
          <Hidden xsDown>
            <Tooltip title={getDateFromTimezonePtBrToGMT(tank.last_rtu_reading_date)}>
              <span className="level">{formatValue(tank.tank_level_su)}</span>
            </Tooltip>
          </Hidden>
        </Grid>
        {authProvider.hasHighLevel() && hasOnePressure? (
          <Grid item xs={2} sm className={classes.spanMinWidth}>
            {tank.tank_pressure && tank.tank_pressure_uom
              ? `${tank.tank_pressure} ${tank.tank_pressure_uom}`
              : ""}
          </Grid>
        ) : null}
      </Grid>
    );
  };

  const autonomyOutage = React.useMemo(() => {
    return installation.autonomy_hours_to_outage
      ? Number(installation.autonomy_hours_to_outage)
      : 0;
  }, [installation]);

  const hasOnePressure = React.useMemo(() => {
    return installation?.tanks?.some(x => x.tank_pressure!= null && x.tank_pressure_uom != null);
  }, [installation?.tanks]);

  const warnings = RenderTanksWarnings(installation.tanks, nowDate);

  const renderWindowSection = () => (
    <div className="window-section">
      <hr className="window-divider" />
      <div className="window-title">Próxima Entrega Prevista:</div>
      <div className="window-row">
        <div className={clsx("flex-wrapper", "align-start")}>
          <div className="label">Início da Janela<br />de Atendimento</div>
          <div className={clsx("label", "window-date")}>
            <strong>{formatDateTimeBr(installation.winow_start)}</strong>
            <strong>{formatDateTimeBr(installation.winow_start, true)}</strong>
          </div>
        </div>
        <div className={clsx("flex-wrapper", "align-start")}>
          <div className="label">Fim da Janela<br />de Atendimento</div>
          <div className={clsx("label", "window-date")}>
            <strong>{formatDateTimeBr(installation.window_end)}</strong>
            <strong>{formatDateTimeBr(installation.window_end, true)}</strong>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card elevation={4} className={classes.root}>
      <div className={classes.cardWrapper}>
        <div className={classes.titleWrapper}>
          <SimpleLink
            href={
              hasAnyPermissionOnGroup
                ? `/installation/${customerId}/${installation.installation_id}`
                : "#"
            }
            label={`${installation.product_code} #${installation.installation_id}`}
            tooltip={
              hasAnyPermissionOnGroup
                ? "clique aqui para ser direcionado à tela de detalhes desta instalação"
                : "Este grupo não possui permissões"
            }
          />
          {authProvider.isFreeUser && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.infoButton}
              startIcon={<InfoOutlinedIcon fontSize="small" />}
              onClick={() => setInfoModalOpen(true)}
            >
              Necessita de mais informações?
            </Button>
          )}

          <div className={classes.captionWrapper}>
            {checkTripPlanned(installation.trip_status) && (
              <Tooltip title="A próxima viagem de abastescimento está prevista">
                <div className={classes.captionWrapper}>
                  <TruckIcon className={"truckIcon"} />
                  <Typography className={"caption"}>Viagem Planejada</Typography>
                </div>
              </Tooltip>
            )}

            {checkTripInProgress(installation.trip_status) && (
              <Tooltip title="A viagem de abastecimento está em rota">
                <div className={classes.captionWrapper}>
                  <TruckIcon
                    altVersion
                    className={clsx("truckIcon", "secondary")}
                    color="primary"
                  />
                  <Typography className={clsx("caption", "alt-color")}>Viagem Em Rota</Typography>
                </div>
              </Tooltip>
            )}
          </div>
        </div>

        <Dialog open={infoModalOpen} onClose={() => setInfoModalOpen(false)}>
          <DialogTitle>Necessita de mais informações?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Para mais informações, entre em contato com seu GN.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInfoModalOpen(false)} variant="contained">
              Voltar
            </Button>
          </DialogActions>
        </Dialog>

        <DottedLine />

        <div className={classes.contentWrapper}>
          <div className="name-wrapper">
            <div className="label">Nome Fantasia</div>
            <Tooltip title={installation.name || ""}>
              <div className="name">
                {radixInstallation?.name || installation.name || EMPTY_STRING_LABEL}
              </div>
            </Tooltip>
          </div>

          <div className="main-content">
            <div className="grid-wrapper">
              <div className={clsx("flex-wrapper", "align-start", blurRestrictedData && classes.blurred)}>
                <div className="label">Nível Instantâneo</div>
                <div className="value">
                  <strong>
                    {`${formatValue(installation.installation_level)} ${
                      installation?.product_uom || ""
                    }`}
                  </strong>
                </div>
              </div>

              <div className={clsx("flex-wrapper", blurRestrictedData && classes.blurred)}>
                <div className="label">Nível Percentual</div>
                <div className="main-value">
                  <strong
                    className={"level"}
                    style={{
                      color: getLevelColor(
                        installation.installation_level_status
                          ? installation.installation_level_status
                          : installation.installation_level,
                        thresholds
                      ),
                    }}
                  >
                    {calculateLevelPercentage(
                      installation.installation_level,
                      installation.inventory_capacity_su
                    )}
                    %
                  </strong>
                </div>
              </div>

              <div className={clsx("flex-wrapper", "align-start", blurRestrictedData && classes.blurred)}>
                <div className="label">Capacidade Total</div>
                <div className="value">
                  <strong>
                    {`${formatValue(installation.inventory_capacity_su)} ${
                      installation.product_uom || ""
                    }`}
                  </strong>
                </div>
              </div>

              <div className={clsx("flex-wrapper", blurRestrictedData && classes.blurred)}>
                <div className="label">
                  Última Medição
                  {delay && (
                    <DelayIcon
                      fontSize="small"
                      date={
                        installation.last_rtu_reading_date
                          ? new Date(installation.last_rtu_reading_date)
                          : undefined
                      }
                    />
                  )}
                </div>
                <div className={clsx("label", "date-wrapper")}>
                  <Typography color={delay ? "error" : "inherit"}>
                    <strong>
                      <Hidden smDown>{formatDateTime(installation.last_rtu_reading_date)}</Hidden>
                      <Hidden smUp>
                        {formatDateTime(installation.last_rtu_reading_date, "dd/MM/yyyy")}
                        <div className="hour-mobile">
                          {formatDateTime(installation.last_rtu_reading_date, "HH:mm")}
                        </div>
                      </Hidden>
                    </strong>
                  </Typography>
                </div>
              </div>
              <div
                className={clsx(
                  "flex-wrapper",
                  "align-start",
                  "fix-width",
                  blurRestrictedData && classes.blurred
                )}
              >
                <div className="label">
                  Autonomia estimada{" "}
                  <HelpIcon tooltip="Total de horas/dias previstas até que acabe o produto no tanque, com base no histórico de consumo recente" />
                </div>
                <div className="value">
                  <strong>
                    {autonomyOutage == 360 ? ">" : ""}
                    {onHour
                      ? `${formatValue(autonomyOutage)} horas`
                      : `${(autonomyOutage / 24).toFixed(1)} dias`}
                  </strong>
                  <Switch checked={!onHour} onChange={handleSwitch} />
                </div>
              </div>
              {showWindowInfo && renderWindowSection()}
            </div>
            <div className={clsx(classes.tankWrapper, blurRestrictedData && classes.blurred)}>
              <TankSVG
                opaque={delay}
                showOutsideLabel
                status={installation.installation_level_status}
                value={installation.installation_level}
                capacity={installation.inventory_capacity_su}
                thresholds={thresholds}
              />
              <div className="labels">
                <span className={classes.totalStorage}>Estocagem total</span>
                {authProvider.hasHighLevel() && hasOnePressure &&
                installation?.tanks?.length &&
                installation?.tanks[0].tank_pressure &&
                installation?.tanks[0].tank_pressure_uom ? (
                  <>
                    <span className={classes.PSIpressure}>
                      Pressão:{" "}
                      {installation?.tanks?.length > 1 &&
                      installation?.tanks[0].tank_pressure &&
                      installation?.tanks[0].tank_pressure_uom ? (
                        <span>
                          <HelpIcon tooltip="Essa instalação possui mais de um tanque, para visualizar informações sobre pressão, expanda a visão por tanques." />
                        </span>
                      ) : installation?.tanks[0].tank_pressure_uom ? (
                        `${installation?.tanks[0].tank_pressure} ${installation?.tanks[0].tank_pressure_uom}`
                      ) : (
                        ""
                      )}
                    </span>
                  </>
                ) : null}
                {/* <span>{installation.installation_id}</span> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Accordion className={clsx(classes.expansionWrapper, blurRestrictedData && classes.blurred)}>
        <AccordionSummary
          expandIcon={null}
          onClick={() => !blurRestrictedData && setExpanded(!expanded)}
        >
          <div className={"summaryWrapper"}>
            <div>
              <span>Quantidade de tanques: </span>
              <strong>{installation?.tanks?.length || EMPTY_STRING_LABEL}</strong>
              {warnings && (
                <Tooltip title={warnings} onOpen={() => setNowDate(new Date())}>
                  <ErrorIcon className={"alertIcon"} fontSize="small"></ErrorIcon>
                </Tooltip>
              )}
            </div>
            {expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
            </div>
          </AccordionSummary>

          <AccordionDetails>
            <div className={classes.tankDetailsWrapper}>
              <Grid container>
                <Grid item xs={1} sm={1}>
                  <span>Status</span>
                </Grid>
                <Grid item xs={3} sm>
                  <span>Id</span>
                </Grid>
                <Grid item xs={4} sm={4}></Grid>
                <Grid item xs={1} sm={1}>
                  <span>Nível</span>
                </Grid>
                <Grid item xs={2} sm>
                  <span>Nível {installation.product_uom}</span>
                </Grid>
                {authProvider.hasHighLevel() && hasOnePressure && installation?.tanks?.some((e) => e.tank_pressure) ? (
                  <Grid item xs={2} sm>
                    <span>Pressão</span>
                  </Grid>
                ) : null}
              </Grid>
              {installation?.tanks?.map((tank) => RenderTank(tank))}
            </div>
          </AccordionDetails>
        </Accordion>
    </Card>
  );
}

export default HomeCard;
